// src/components/gamification/Gamification.js
import React, { useState, useEffect } from 'react';
import './Gamification.css';

// XP Configuration (synced with XPSystem.js)
const XP_STORAGE_KEY = 'user_xp_';
const XP_ACTIONS = {
  TICKET_PURCHASE: 50,
  TICKET_CHECKIN: 100,
  PERK_CLAIM: 25,
  FIRST_PURCHASE: 100,
  BATCH_PURCHASE: 20,
  EARLY_BIRD: 75,
};

// Level thresholds
const LEVELS = [
  { level: 1, xpRequired: 0, title: 'Newcomer', color: '#9ca3af' },
  { level: 2, xpRequired: 100, title: 'Fan', color: '#60a5fa' },
  { level: 3, xpRequired: 300, title: 'Regular', color: '#34d399' },
  { level: 4, xpRequired: 600, title: 'Enthusiast', color: '#fbbf24' },
  { level: 5, xpRequired: 1000, title: 'VIP', color: '#f97316' },
  { level: 6, xpRequired: 1500, title: 'Legend', color: '#a855f7' },
  { level: 7, xpRequired: 2500, title: 'Master', color: '#ec4899' },
  { level: 8, xpRequired: 4000, title: 'Champion', color: '#ef4444' },
  { level: 9, xpRequired: 6000, title: 'Elite', color: '#8b5cf6' },
  { level: 10, xpRequired: 10000, title: 'Hall of Fame', color: '#ffd700' },
];

// Get user's XP data from localStorage
function getXPData(account) {
  if (!account) return null;

  try {
    const storageKey = XP_STORAGE_KEY + account.toLowerCase();
    const data = localStorage.getItem(storageKey);

    if (!data) {
      return { totalXP: 0, level: 1, actions: [], achievements: [] };
    }

    return JSON.parse(data);
  } catch (error) {
    console.error('[Gamification] Error reading XP data:', error);
    return null;
  }
}

// Calculate level from total XP
function calculateLevel(totalXP) {
  for (let i = LEVELS.length - 1; i >= 0; i--) {
    if (totalXP >= LEVELS[i].xpRequired) {
      return LEVELS[i];
    }
  }
  return LEVELS[0];
}

// Get rewards for level
function getRewardsForLevel(level) {
  const rewards = [];
  if (level >= 1) rewards.push({ icon: '🎟️', text: 'Early access to events' });
  if (level >= 2) rewards.push({ icon: '🎁', text: '5% merchandise discount' });
  if (level >= 3) rewards.push({ icon: '⭐', text: 'Priority customer support' });
  if (level >= 4) rewards.push({ icon: '🎤', text: 'Exclusive content access' });
  if (level >= 5) rewards.push({ icon: '💎', text: 'VIP lounge access' });
  if (level >= 6) rewards.push({ icon: '🎪', text: 'Free event upgrades' });
  if (level >= 7) rewards.push({ icon: '🏆', text: 'Backstage passes' });
  if (level >= 8) rewards.push({ icon: '👑', text: 'Meet & greet access' });
  if (level >= 9) rewards.push({ icon: '🌟', text: 'Lifetime benefits' });
  if (level >= 10) rewards.push({ icon: '🔥', text: 'Hall of Fame status' });
  return rewards;
}

export default function Gamification({ ticket, account }) {
  const [xpData, setXpData] = useState(null);
  const [currentLevel, setCurrentLevel] = useState(null);
  const [nextLevel, setNextLevel] = useState(null);
  const [progressPercent, setProgressPercent] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Get account from window.ethereum if not provided
  const userAccount = account || (typeof window !== 'undefined' && window.ethereum?.selectedAddress);

  // Function to load XP data
  const loadXPData = (acc) => {
    if (!acc) return;

    const data = getXPData(acc);
    if (!data) return;

    setXpData(data);

    const level = calculateLevel(data.totalXP);
    setCurrentLevel(level);

    // Find next level
    const nextLevelIndex = LEVELS.findIndex(l => l.level === level.level + 1);
    if (nextLevelIndex !== -1) {
      const next = LEVELS[nextLevelIndex];
      setNextLevel(next);

      // Calculate progress percentage
      const currentLevelXP = level.xpRequired;
      const nextLevelXP = next.xpRequired;
      const xpIntoLevel = data.totalXP - currentLevelXP;
      const xpNeededForNext = nextLevelXP - currentLevelXP;
      const percent = (xpIntoLevel / xpNeededForNext) * 100;
      setProgressPercent(Math.min(100, Math.max(0, percent)));
    } else {
      // Max level reached
      setNextLevel(null);
      setProgressPercent(100);
    }
  };

  // Initial load and refresh when account changes
  useEffect(() => {
    loadXPData(userAccount);
  }, [userAccount, refreshKey]);

  // Listen for localStorage changes (XP updates from other components)
  useEffect(() => {
    if (!userAccount) return;

    const storageKey = XP_STORAGE_KEY + userAccount.toLowerCase();

    const handleStorageChange = (e) => {
      if (e.key === storageKey) {
        console.log('[Gamification] XP data changed, refreshing...');
        setRefreshKey(prev => prev + 1);
      }
    };

    // Listen for storage events (cross-tab)
    window.addEventListener('storage', handleStorageChange);

    // Also poll for changes within the same tab (localStorage doesn't fire events for same-tab changes)
    const pollInterval = setInterval(() => {
      const currentData = getXPData(userAccount);
      if (currentData && xpData && currentData.totalXP !== xpData.totalXP) {
        console.log('[Gamification] XP data changed (poll), refreshing...');
        loadXPData(userAccount);
      }
    }, 2000); // Check every 2 seconds

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(pollInterval);
    };
  }, [userAccount, xpData]);

  // No account connected or no XP data
  if (!userAccount || !xpData || !currentLevel) {
    return (
      <div className="gamification gamification-placeholder">
        <div className="gamification-header">
          <h3>🎮 XP & Rewards</h3>
        </div>
        <p className="gamification-connect-msg">
          Connect wallet to view your XP and rewards
        </p>
      </div>
    );
  }

  const xpNeeded = nextLevel ? nextLevel.xpRequired - xpData.totalXP : 0;
  const rewards = getRewardsForLevel(currentLevel.level);

  return (
    <div className="gamification">
      <div className="gamification-header" onClick={() => setShowDetails(!showDetails)}>
        <h3>🎮 XP & Rewards</h3>
        <span className="gamification-toggle">{showDetails ? '▲' : '▼'}</span>
      </div>

      {/* XP Bar */}
      <div className="xp-bar-container">
        <div className="xp-level-badge" style={{ borderColor: currentLevel.color, color: currentLevel.color }}>
          <span className="level-number">Lv.{currentLevel.level}</span>
          <span className="level-title">{currentLevel.title}</span>
        </div>

        <div className="xp-progress-wrapper">
          <div className="xp-bar">
            <div
              className="xp-progress"
              style={{
                width: `${progressPercent}%`,
                background: `linear-gradient(90deg, ${currentLevel.color}, ${nextLevel?.color || currentLevel.color})`
              }}
            />
          </div>
          <div className="xp-info">
            <span className="xp-current">{xpData.totalXP} XP</span>
            {nextLevel ? (
              <span className="xp-next">{xpNeeded} XP to {nextLevel.title}</span>
            ) : (
              <span className="xp-max">✨ MAX LEVEL</span>
            )}
          </div>
        </div>
      </div>

      {/* Rewards Section */}
      <div className="rewards">
        <h4>🏆 Your Rewards ({rewards.length})</h4>
        <div className="rewards-list">
          {rewards.slice(0, showDetails ? rewards.length : 3).map((reward, index) => (
            <div key={index} className="reward-item">
              <span className="reward-icon">{reward.icon}</span>
              <span className="reward-text">{reward.text}</span>
            </div>
          ))}
          {!showDetails && rewards.length > 3 && (
            <div className="reward-more">+{rewards.length - 3} more rewards</div>
          )}
        </div>
      </div>

      {/* Detailed View */}
      {showDetails && (
        <div className="gamification-details">
          {/* Recent Activity */}
          <div className="recent-activity">
            <h4>📊 Recent Activity</h4>
            {xpData.actions.length === 0 ? (
              <p className="no-activity">Purchase tickets to start earning XP!</p>
            ) : (
              <div className="activity-list">
                {xpData.actions.slice(-5).reverse().map((action, index) => (
                  <div key={index} className="activity-item">
                    <span className="activity-name">{formatActionName(action.action)}</span>
                    <span className="activity-xp">+{action.xp} XP</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* How to Earn XP */}
          <div className="earn-xp">
            <h4>💡 How to Earn XP</h4>
            <div className="earn-list">
              <div className="earn-item">
                <span>Purchase Ticket</span>
                <span className="earn-amount">+{XP_ACTIONS.TICKET_PURCHASE} XP</span>
              </div>
              <div className="earn-item">
                <span>Check-in to Event</span>
                <span className="earn-amount">+{XP_ACTIONS.TICKET_CHECKIN} XP</span>
              </div>
              <div className="earn-item">
                <span>Claim Perk</span>
                <span className="earn-amount">+{XP_ACTIONS.PERK_CLAIM} XP</span>
              </div>
              <div className="earn-item">
                <span>First Purchase Bonus</span>
                <span className="earn-amount">+{XP_ACTIONS.FIRST_PURCHASE} XP</span>
              </div>
              <div className="earn-item">
                <span>Batch Purchase (3+)</span>
                <span className="earn-amount">+{XP_ACTIONS.BATCH_PURCHASE} XP/ticket</span>
              </div>
              <div className="earn-item">
                <span>Early Bird (7+ days)</span>
                <span className="earn-amount">+{XP_ACTIONS.EARLY_BIRD} XP</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function to format action names
function formatActionName(action) {
  const names = {
    TICKET_PURCHASE: '🎟️ Ticket Purchased',
    TICKET_CHECKIN: '✅ Event Check-in',
    PERK_CLAIM: '🎁 Perk Claimed',
    FIRST_PURCHASE: '🌟 First Purchase',
    BATCH_PURCHASE: '📦 Batch Purchase',
    EARLY_BIRD: '🐦 Early Bird',
    REFERRAL: '👥 Referral',
    RETROACTIVE_SYNC: '🔄 Existing Tickets Sync'
  };
  return names[action] || action;
}